import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import SEOMeta from "../../components/SEOMeta";
import styles from "../../styles/Button.module.css";
import { useLocation } from "react-router-dom";

// GitHub Internal Repository Breach - May 19, 2026
// VERIFIED SOURCES:
// - GitHub Official Statement on X (Twitter)
// - Confirmed reporting: SecurityWeek, Infosecurity Magazine, iTnews
//
// NOTE: TeamPCP's $50,000 ransom claim and specific repo counts (~3,800-4,000) 
// are reported by news outlets and largely confirmed by GitHub, but the exact 
// details of the extension and employee remain undisclosed.

const GitHubBreachIncident: React.FC = () => {
  const adContainerRef1 = useRef<HTMLDivElement>(null);
  const adContainerRef2 = useRef<HTMLDivElement>(null);
  const adInitialized1 = useRef(false);
  const adInitialized2 = useRef(false);
  const location = useLocation();

  // ==================== STRUCTURED DATA FOR SEO ENHANCEMENTS ====================
  
  // Article structured data
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    "headline": "GitHub Data Breach 2026: 3,800 Internal Repos Stolen via Malicious VS Code Extension",
    "alternativeHeadline": "GitHub Confirms Supply Chain Attack via Poisoned IDE Plugin",
    "description": "On May 19, 2026, GitHub confirmed a security breach where attackers used a poisoned VS Code extension to steal ~3,800 internal repositories. The TeamPCP hacking group claimed responsibility and demanded $50,000 for the stolen source code.",
    "datePublished": "2026-05-19T08:00:00+00:00",
    "dateModified": "2026-05-20T12:00:00+00:00",
    "author": {
      "@type": "Person",
      "name": "Cybersecurity Student",
      "url": "https://cyberannals.com/about"
    },
    "publisher": {
      "@type": "Person",
      "name": "Cybersecurity Student",
      "url": "https://cyberannals.com/about"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://cyberannals.com/blog/github-data-breach-2026"
    },
    "keywords": "GitHub breach, data breach, supply chain attack, VS Code extension, TeamPCP, IDE compromise, source code theft",
    "articleSection": "Cybersecurity Incidents",
    "inLanguage": "en-US",
    "isAccessibleForFree": true
  };

  // Breadcrumb structured data for rich results in search
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://cyberannals.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://cyberannals.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "GitHub Data Breach 2026",
        "item": "https://cyberannals.com/blog/github-data-breach-2026"
      }
    ]
  };

  // WebPage structured data
  const webpageStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "GitHub Data Breach 2026: 3,800 Internal Repos Stolen via VS Code Extension",
    "description": "GitHub confirms major internal source code breach on May 19, 2026. Attackers used a poisoned VS Code extension to steal ~3,800 repositories. TeamPCP demands $50,000. Full incident analysis and security recommendations.",
    "url": "https://cyberannals.com/blog/github-data-breach-2026",
    "datePublished": "2026-05-19",
    "dateModified": "2026-05-20",
    "inLanguage": "en-US"
  };

  // Combined structured data for single script tag
  const combinedStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      articleStructuredData,
      breadcrumbStructuredData,
      webpageStructuredData
    ]
  };

  // Verified timeline events (from GitHub official statement and confirmed reports)
  const timelineEvents = [
    {
      time: "May 18, 2026, 12:30 UTC",
      event: "Malicious Extension Published",
      description: "A poisoned version of the Nx Console VS Code extension (v18.95.0) was published to the Visual Studio Marketplace and remained live for approximately 18 minutes",
      verified: true
    },
    {
      time: "May 19, 2026",
      event: "Unauthorized Access Detected",
      description: "GitHub detected and contained a compromise on an employee device involving the trojanized VS Code extension",
      verified: true
    },
    {
      time: "May 20, 2026 (Morning)",
      event: "Official Confirmation & Credential Rotation",
      description: "GitHub confirmed the breach publicly, rotated critical secrets, and disclosed that ~3,800 internal repositories were exfiltrated",
      verified: true
    },
    {
      time: "Post-Discovery",
      event: "Investigation Continues",
      description: "GitHub engaged security teams, isolated the endpoint, and committed to publishing a full incident report",
      verified: true
    }
  ];

  // Load Google AdSense ads
  useEffect(() => {
    // FIRST AD: In-article / Fluid format
    if (adContainerRef1.current && typeof window !== 'undefined' && !adInitialized1.current) {
      adInitialized1.current = true;
      adContainerRef1.current.innerHTML = '';
      const ins = document.createElement('ins');
      ins.className = 'adsbygoogle';
      ins.style.display = 'block';
      ins.style.textAlign = 'center';
      ins.style.minWidth = '300px';
      ins.style.minHeight = '250px';
      ins.setAttribute('data-ad-client', 'ca-pub-9325373695328890');
      ins.setAttribute('data-ad-slot', '3721511676');
      ins.setAttribute('data-ad-layout', 'in-article');
      ins.setAttribute('data-ad-format', 'fluid');
      adContainerRef1.current.appendChild(ins);
      setTimeout(() => {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
          console.error("Adsense error:", e);
        }
      }, 500);
    }

    // SECOND AD: Display / Auto format
    if (adContainerRef2.current && typeof window !== 'undefined' && !adInitialized2.current) {
      adInitialized2.current = true;
      adContainerRef2.current.innerHTML = '';
      const ins = document.createElement('ins');
      ins.className = 'adsbygoogle';
      ins.style.display = 'block';
      ins.setAttribute('data-ad-client', 'ca-pub-9325373695328890');
      ins.setAttribute('data-ad-slot', '1789827060');
      ins.setAttribute('data-ad-format', 'auto');
      ins.setAttribute('data-full-width-responsive', 'true');
      adContainerRef2.current.appendChild(ins);
      setTimeout(() => {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
          console.error("Adsense error:", e);
        }
      }, 500);
    }
  }, [location.pathname]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-12">
      {/* SEOMeta with enhanced keywords */}
      <SEOMeta
        title="GitHub Data Breach 2026"
        description="GitHub confirms major data breach on May 19, 2026. Attackers stole ~3,800 internal repositories via a poisoned VS Code extension. TeamPCP demands $50,000. Full incident analysis and security recommendations for developers."
        keywords="GitHub breach, GitHub data breach 2026, GitHub hacked, GitHub security incident, GitHub May 19 2026, VS Code extension breach, TeamPCP GitHub, Nx Console malicious extension, GitHub internal repos stolen, source code theft, supply chain attack, IDE compromise, GitHub employee device hacked, GitHub secret rotation, GitHub Copilot breach, Microsoft GitHub hack, TeamPCP supply chain, developer workstation compromise, poisoned VS Code extension, GitHub source code exfiltration"
        canonical="https://cyberannals.com/blog/github-data-breach-2026"
        ogType="article"
        structuredData={combinedStructuredData}
      />

      {/* Twitter Card meta tags for better social sharing */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@CyberAnnals" />
      <meta name="twitter:title" content="GitHub Data Breach 2026: 3,800 Internal Repos Stolen via VS Code Extension" />
      <meta name="twitter:description" content="GitHub confirms major internal source code breach on May 19, 2026. TeamPCP used a poisoned VS Code extension to exfiltrate ~3,800 repositories. $50,000 demanded." />
      <meta name="twitter:image" content="https://cyberannals.com/images/github-breach-og.jpg" />

      {/* First Advertisement - In-Article / Fluid */}
      <div className="lg:col-span-4 mb-6">
        <div className="text-xs text-slate-400 dark:text-slate-500 mb-2 px-1">Advertisement</div>
        <div className="h-full min-h-[250px] min-w-[250px] w-full bg-white/5 border border-white/10 rounded-2xl relative overflow-hidden"
             id="github-breach-ad-1"
             ref={adContainerRef1}>
        </div>
      </div>

      {/* Breaking News Banner */}
      <div className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-3 rounded-lg mb-6 animate-pulse">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="font-bold text-lg sm:text-xl flex items-center gap-2">
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            CONFIRMED: GitHub Internal Repository Breach - May 19, 2026
          </span>
          <span className="text-sm sm:text-base bg-white text-blue-600 px-3 py-1 rounded-full font-semibold">
            Official
          </span>
        </div>
      </div>

      <article className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
        {/* Header Section */}
        <header className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 dark:text-white mb-3 sm:mb-4 leading-tight">
            GitHub Data Breach 2026
          </h1>
          <h2 className="text-lg sm:text-xl text-slate-600 dark:text-slate-400 mb-4">
            Hackers Steal ~3,800 Internal Repositories by Poisoning a Single Developer's VS Code Extension
          </h2>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              May 19, 2026
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Single Malicious IDE Extension
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              5 min read
            </span>
          </div>
        </header>

        {/* Verified Key Alert Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-red-800 dark:text-red-200 text-sm">Attack Vector</h3>
                <p className="text-red-600 dark:text-red-300 text-sm font-medium">Poisoned VS Code Extension</p>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-orange-800 dark:text-orange-200 text-sm">Data Stolen</h3>
                <p className="text-orange-600 dark:text-orange-300 text-sm font-medium">~3,800 Internal Repositories</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-green-800 dark:text-green-200 text-sm">Customer Data</h3>
                <p className="text-green-600 dark:text-green-300 text-sm font-medium">No Evidence of Impact</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-purple-800 dark:text-purple-200 text-sm">Threat Actor</h3>
                <p className="text-purple-600 dark:text-purple-300 text-sm font-medium">TeamPCP (UNC6780)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-4 sm:space-y-6 text-slate-700 dark:text-slate-300">
          
          {/* Lead Paragraph - summary for featured snippets */}
          <p className="text-base sm:text-lg font-medium summary">
            On <strong>May 19, 2026, GitHub confirmed a major security breach</strong> affecting approximately <strong>3,800 internal repositories</strong> [citation:3]. The attack was traced back to a single employee's installation of a malicious Visual Studio Code (VS Code) extension, marking the latest in a series of supply chain attacks targeting developer tooling [citation:1].
          </p>

          {/* Official Confirmation Box */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-green-900 dark:text-green-100 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              ✅ What GitHub Has Officially Confirmed
            </h2>
            <div className="space-y-3 text-green-800 dark:text-green-200">
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>An employee device was compromised through a "poisoned" VS Code extension [citation:3]</li>
                <li>Approximately <strong>3,800 internal GitHub repositories</strong> were exfiltrated [citation:7]</li>
                <li>The breach was detected and contained on May 19, 2026 [citation:3]</li>
                <li>Critical secrets were rotated with the highest-impact credentials prioritized first [citation:7]</li>
                <li>The malicious extension version was removed from the VS Code Marketplace [citation:2]</li>
                <li>No evidence of impact to customer data or private repositories outside internal systems [citation:7]</li>
                <li>The attacker claims of ~3,800 repositories are "directionally consistent" with GitHub's investigation [citation:7]</li>
                <li>GitHub will publish a full incident report once the investigation concludes [citation:7]</li>
              </ul>
            </div>
          </div>

          {/* What This Means For You */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-100 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              📧 What This Means For GitHub Users
            </h2>
            <div className="space-y-3 text-blue-800 dark:text-blue-200">
              <p>Based on GitHub's official statements and CISO Alexis Wales:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong>Customer private repositories are safe:</strong> "No evidence of impact to customer information stored outside of GitHub's internal repositories" [citation:7]</li>
                <li>Some internal repositories may contain excerpts from customer support interactions; GitHub will notify affected customers if any impact is discovered [citation:4]</li>
                <li><strong>GitHub Copilot</strong> and customer-facing services were not compromised</li>
                <li>All critical secrets have been rotated and infrastructure is being closely monitored for follow-on activity [citation:7]</li>
              </ul>
            </div>
          </div>

          {/* Verified Timeline */}
          <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-4">Verified Attack Timeline</h2>
            <div className="space-y-3">
              {timelineEvents.map((event, index) => (
                <div key={index} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 pb-3 border-b border-slate-200 dark:border-slate-700 last:border-0">
                  <span className="font-mono text-sm bg-slate-200 dark:bg-slate-700 px-2 py-1 rounded text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    {event.time}
                  </span>
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white">{event.event}</h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{event.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Second Advertisement - Display / Auto */}
          <div className="my-6">
            <div className="text-xs text-slate-400 dark:text-slate-500 mb-2 px-1">Advertisement</div>
            <div className="h-full min-h-[250px] min-w-[250px] w-full bg-white/5 border border-white/10 rounded-2xl relative overflow-hidden"
                 id="github-breach-ad-2"
                 ref={adContainerRef2}>
            </div>
          </div>

          {/* Attack Mechanism Box */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-purple-900 dark:text-purple-100 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              ⚙️ Attack Mechanism: The VS Code Extension Blind Spot
            </h2>
            
            <div className="space-y-4 text-purple-800 dark:text-purple-200">
              <p>
                The GitHub breach exposed a critical security gap known as the "extension blind spot" [citation:1]. VS Code extensions run with <strong>full user privileges</strong> on the developer's workstation, with unrestricted access to credentials, SSH keys, cloud keys, and all secrets in that environment. Once installed, a malicious extension requires no privilege escalation — the installation itself is the compromise [citation:2].
              </p>
              
              <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-purple-100 dark:border-purple-800">
                <p className="italic">
                  "Developer workstations are the number one target in supply chain attacks right now... A single VS Code extension on one employee's machine was enough to get access to 3,800 internal GitHub repositories."
                </p>
                <p className="text-right text-sm mt-2">— Mackenzie Jackson, Aikido Security [citation:7]</p>
              </div>

              <p className="text-sm">
                This incident was part of a larger campaign. The malicious extension was linked to the <strong>Mini Shai-Hulud campaign</strong>, which also affected OpenAI (two employee devices compromised) and Mistral AI through the same npm supply chain attack [citation:6].
              </p>
            </div>
          </div>

          {/* Unverified Claims Section - TeamPCP Ransom */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 rounded-r-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-yellow-800 dark:text-yellow-200 mb-3 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              ⚠️ TeamPCP's Ransom & Claims (Partially Verified by GitHub)
            </h2>
            <div className="space-y-3 text-yellow-800 dark:text-yellow-200">
              <p>Hacking group <strong>TeamPCP</strong> (tracked as UNC6780 by Google Threat Intelligence) claimed responsibility on the Breached cybercrime forum. <strong>GitHub confirmed the group's claims are "directionally consistent."</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong>$50,000 minimum price</strong> - TeamPCP demanded at least $50,000 for the stolen source code, stating they were "not interested in under 50k" [citation:3]</li>
                <li><strong>Single buyer only</strong> - The group claimed they would sell to one buyer and delete the data afterward [citation:3]</li>
                <li><strong>Free leak threat</strong> - TeamPCP warned that if no buyer was found, they would release the data publicly [citation:5]</li>
                <li><strong>"Not a ransom"</strong> - The group explicitly stated they were not extorting GitHub, framing it as a sale rather than ransomware [citation:3]</li>
              </ul>
              <p className="text-sm mt-2 font-bold">TeamPCP's Recent Supply Chain Attacks:</p>
              <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
                <li>Aqua Security Trivy (March 2026) - Compromised GitHub Actions tags [citation:1]</li>
                <li>Checkmarx KICS (March 2026) - Malicious VS Code plugins on OpenVSX [citation:1]</li>
                <li>LiteLLM PyPI package (March 2026) - Compromised maintainer credentials [citation:1]</li>
                <li>TanStack + Mistral AI (May 2026) - Legitimate release pipeline abused [citation:1]</li>
              </ul>
            </div>
          </div>

          {/* Technical Details */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-4">Technical Details: The Nx Console Compromise</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-slate-700 p-4 rounded-lg">
                <h3 className="font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Attack Chain (Confirmed)
                </h3>
                <p className="text-sm">TanStack npm compromise → Nx Console developer credentials stolen → Malicious VS Code extension (v18.95.0) published → GitHub employee installs extension → Credentials harvested → 3,800 repositories exfiltrated [citation:6]</p>
              </div>
              
              <div className="bg-white dark:bg-slate-700 p-4 rounded-lg">
                <h3 className="font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Extension Details
                </h3>
                <p className="text-sm"><strong>Extension:</strong> Nx Console (nrwl.angular-console) [citation:4]</p>
                <p className="text-sm"><strong>Malicious Version:</strong> 18.95.0</p>
                <p className="text-sm"><strong>Live Window:</strong> ~18 minutes (12:30-12:48 UTC, May 18) [citation:6]</p>
                <p className="text-sm"><strong>Installations:</strong> 2.2 million+ [citation:4]</p>
              </div>
            </div>
          </div>

          {/* Security Recommendations */}
          <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-600 p-4 sm:p-6 rounded-r-lg mt-6">
            <h3 className="font-bold text-lg text-red-800 dark:text-red-200 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              🛡️ Security Recommendations for Developers & Organizations
            </h3>
            <div className="space-y-3 text-red-700 dark:text-red-300">
              <p><strong>Based on analysis from security researchers and the pattern of recent supply chain attacks:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong>Deploy VS Code's AllowedExtensions policy</strong> via Intune or GPO to restrict which extensions can be installed [citation:1]</li>
                <li><strong>Build an extension inventory</strong> across your development team and regularly audit installed plugins</li>
                <li><strong>Isolate credentials</strong> with short-lived tokens and avoid storing production secrets on developer workstations</li>
                <li><strong>Include developer machines in threat hunting</strong> — these are now the primary target for supply chain attacks [citation:7]</li>
                <li><strong>Verify extension publishers</strong> before installing and be cautious even of verified publishers (Nx Console had verified status) [citation:2]</li>
                <li><strong>Monitor the VS Code Marketplace</strong> for suspicious extension updates, especially those published outside normal release schedules</li>
              </ul>
              <div className="bg-white dark:bg-slate-800 rounded-lg p-3 mt-3">
                <p className="text-sm italic">
                  "This incident reflects a broader shift in the threat landscape — attackers are increasingly targeting shared software dependencies and development tooling rather than any single company."
                </p>
                <p className="text-right text-xs mt-1">— OpenAI statement on the Mini Shai-Hulud campaign [citation:6]</p>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-4">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">Was GitHub hacked in 2026?</h3>
                <p className="text-slate-700 dark:text-slate-300">Yes, GitHub confirmed on May 19, 2026 that an employee device was compromised through a poisoned VS Code extension, leading to the theft of ~3,800 internal repositories [citation:3].</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">Were GitHub customer repositories affected?</h3>
                <p className="text-slate-700 dark:text-slate-300">No, GitHub has found no evidence of impact to customer data stored outside of GitHub's internal repositories. This includes private user repos, enterprise environments, and organizations [citation:7].</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">Who attacked GitHub?</h3>
                <p className="text-slate-700 dark:text-slate-300">The TeamPCP hacking group (tracked as UNC6780 by Google Threat Intelligence) claimed responsibility. They are known for a series of supply chain attacks targeting developer tooling [citation:7].</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">What is the VS Code extension blind spot?</h3>
                <p className="text-slate-700 dark:text-slate-300">VS Code extensions run with full user privileges and unrestricted access to everything on a developer's machine. Most organizations have no governance, monitoring, or inventory of installed extensions, creating a security gap attackers are actively exploiting [citation:1].</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">Should I stop using VS Code extensions?</h3>
                <p className="text-slate-700 dark:text-slate-300">Not necessarily, but you should audit installed extensions, deploy organizational policies to restrict allowed extensions, and be cautious about auto-updates from the marketplace [citation:1].</p>
              </div>
            </div>
          </div>

          {/* Current Status */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-600 p-4 sm:p-6 rounded-r-lg mt-6">
            <h3 className="font-bold text-lg text-amber-800 dark:text-amber-200 mb-2">
              📌 Current Status - As of May 20, 2026
            </h3>
            <ul className="list-disc list-inside text-amber-700 dark:text-amber-300 space-y-2">
              <li><strong>Breach contained</strong> - Malicious extension removed and endpoint isolated [citation:3]</li>
              <li><strong>Credential rotation complete</strong> - Critical secrets rotated with highest-impact credentials prioritized [citation:7]</li>
              <li><strong>Ongoing investigation</strong> - GitHub analyzing logs, validating secret rotation, and monitoring for follow-on activity [citation:7]</li>
              <li><strong>Full incident report</strong> will be published once the investigation concludes [citation:7]</li>
              <li><strong>Customer-facing services remain fully operational</strong> with no disruption</li>
            </ul>
          </div>

          {/* Related Historical Incidents */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-6 mt-6">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-4">Related Security Incidents</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link to="/blog/vercel-data-breach-2026" className="block p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                <span className="font-semibold text-slate-900 dark:text-white">🔺 Vercel Data Breach - April 2026</span>
                <p className="text-sm text-slate-600 dark:text-slate-400">Third-party AI tool compromise exposed environment variables</p>
              </Link>
              <Link to="/event/kaseya-vsa-2021" className="block p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                <span className="font-semibold text-slate-900 dark:text-white">🔐 Kaseya VSA Ransomware Attack</span>
                <p className="text-sm text-slate-600 dark:text-slate-400">Supply chain attack affecting hundreds of businesses</p>
              </Link>
              <Link to="/event/microsoft-solarwinds-sunburst" className="block p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                <span className="font-semibold text-slate-900 dark:text-white">🎯 SolarWinds Sunburst Attack</span>
                <p className="text-sm text-slate-600 dark:text-slate-400">Major software supply chain compromise</p>
              </Link>
              <Link to="/event/log4j-log4shell-2021" className="block p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                <span className="font-semibold text-slate-900 dark:text-white">📦 Log4Shell Vulnerability</span>
                <p className="text-sm text-slate-600 dark:text-slate-400">Critical zero-day affecting millions of applications</p>
              </Link>
            </div>
          </div>
        </div>

        {/* Footer Navigation */}
        <footer className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-slate-200 dark:border-slate-700">
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <Link
              to="/blog"
              className="inline-flex items-center px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              ← Back to Blog
            </Link>
            <Link
              to="/decade/2020-2026"
              className="inline-flex items-center px-3 py-2 text-sm bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded-lg hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
            >
              More 2026 Cyber Attacks →
            </Link>
          </div>

          <div className="mt-6">
            <h3 className="font-bold text-slate-900 dark:text-white mb-3 text-sm sm:text-base">Related Coverage:</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Link to="/decade/2020-2026" className={styles.link}>2026 Cyber Attacks</Link>
              <Link to="/event/supply-chain-attacks" className={styles.link}>Supply Chain Security</Link>
              <Link to="/blog/ai-driven-cyber-attacks" className={styles.link}>IDE Security Risks</Link>
              <Link to="/toolkit/devsecops" className={styles.link}>DevSecOps Best Practices</Link>
              <Link to="/blog/zero-trust-architecture" className={styles.link}>Zero Trust Architecture</Link>
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default GitHubBreachIncident;
